import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

export type PipeEccentricReducerAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PipeEccentricReducer
  length: number
  inlet_radius: number
  outlet_radius: number
  material: string | undefined
  installation_date: string | undefined
  features: Record<string, GeckoVerticalWeldJSON>

  clock_bearing_rotation: number | undefined
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const PipeEccentricReducerAvailableFeatures: GeckoFeatureModelType[] = []

export const NewPipeEccentricReducerAssetModelJSON = {
  name: 'New Pipe Eccentric Reducer',
  type: GeckoComponentModelType.PipeEccentricReducer,
  length: 0,
  inlet_radius: 0,
  outlet_radius: 0,
  material: undefined,
  installation_date: undefined,
  features: {},

  clock_bearing_rotation: 0,
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as PipeEccentricReducerAssetModelJSON

export class GeckoPipeEccentricReducer extends GeckoEditableComponent {
  type: GeckoComponentModelType.PipeEccentricReducer
  length: GeckoMeasurement
  inlet_radius: GeckoMeasurement
  outlet_radius: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  features: Record<string, GeckoVerticalWeldJSON>

  clockBearingRotation: number | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    length: GeckoMeasurement,
    inlet_radius: GeckoMeasurement,
    outlet_radius: GeckoMeasurement,
    material: string | undefined,
    installationDate: Date | undefined,

    clockBearingRotation: number | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.PipeEccentricReducer
    this.length = length
    this.inlet_radius = inlet_radius
    this.outlet_radius = outlet_radius
    this.material = material
    this.installationDate = installationDate
    this.features = {}
    this.clockBearingRotation = clockBearingRotation
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public static CreateFromJSON(
    id: string,
    json: PipeEccentricReducerAssetModelJSON
  ) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const length = GeckoMeasurement.CreateFromValue(
      json.length,
      GeckoMeasurementUnit.Meters
    )
    const inletRadius = GeckoMeasurement.CreateFromValue(
      json.inlet_radius,
      GeckoMeasurementUnit.Meters
    )
    const outletRadius = GeckoMeasurement.CreateFromValue(
      json.outlet_radius,
      GeckoMeasurementUnit.Meters
    )

    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const clockBearingRotation = parseNumber(json.clock_bearing_rotation)
    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoPipeEccentricReducer(
      id,
      json,
      name,
      length,
      inletRadius,
      outletRadius,
      material,
      installationDate,
      clockBearingRotation,
      componentBounds,
      sceneConfig,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): PipeEccentricReducerAssetModelJSON {
    return generatePipeEccentricReducer(
      this.name,
      this.length,
      this.inlet_radius,
      this.outlet_radius,
      this.material,
      this.installationDate,
      this.clockBearingRotation,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as PipeEccentricReducerAssetModelJSON
    )
  }
}

const generatePipeEccentricReducer = (
  name: string,
  length: GeckoMeasurement,
  inletRadius: GeckoMeasurement,
  outletRadius: GeckoMeasurement,
  material: string | undefined,
  installationDate: Date | undefined,
  clockBearingRotation: number | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherPipeEccentricReducerData: PipeEccentricReducerAssetModelJSON = NewPipeEccentricReducerAssetModelJSON
): PipeEccentricReducerAssetModelJSON => {
  const pipeLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeInletRadius = inletRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeOutletRadius = outletRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // Component bounds is a rotation around the larger end radius
  const eccentricReducerCircumference = GeckoMeasurement.CreateFromValue(
    Math.max(pipeInletRadius, pipeOutletRadius) * 2 * pi,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PipeEccentricReducer,
    length: pipeLength,
    inlet_radius: pipeInletRadius,
    outlet_radius: pipeOutletRadius,
    features: {},
    material: material,
    installation_date: parsedInstallationDate,
    clock_bearing_rotation: clockBearingRotation,
    scene_config: otherPipeEccentricReducerData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: pipeLength,
      y_end: eccentricReducerCircumference,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const PipeEccentricReducerFormFields = (
  pipeEccentricReducerJSON: PipeEccentricReducerAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Pipe Eccentric Reducer Name',
      value: pipeEccentricReducerJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: pipeEccentricReducerJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'inlet_radius',
      label: 'Inlet Radius',
      value: pipeEccentricReducerJSON.inlet_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'outlet_radius',
      label: 'Outlet Length',
      value: pipeEccentricReducerJSON.outlet_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: pipeEccentricReducerJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(pipeEccentricReducerJSON.installation_date, true),
    {
      dataKey: 'clock_bearing_rotation',
      label: 'Clock Rotation',
      value: pipeEccentricReducerJSON.clock_bearing_rotation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(pipeEccentricReducerJSON.nominal.wall_thickness),
    tminFormField(pipeEccentricReducerJSON.tmin),
    compassOrientationFormField(pipeEccentricReducerJSON.compass_orientation),
  ]
}

export const PipeEccentricReducerJSONFromFormFields = (
  component: PipeEccentricReducerAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )

  return generatePipeEccentricReducer(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'inlet_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'outlet_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parseNumber(
      getFormFieldByDataKey(formFields, 'clock_bearing_rotation')?.value
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
